import { PublicClientApplication } from "@azure/msal-browser";
import { instance } from "./apiRequest";
import { SSO_VALIDATE_MICROSOFT_TOKEN } from "../constants/constants";

let _msalInstance        = null;
let _activeClientId      = null;
// undefined = not yet called; null = called, no result; object = result
let _redirectResultCache = undefined;

export async function getMsalInstance() {
    const stored = localStorage.getItem("tenantInfo");
    if (!stored) throw new Error("Tenant configuration not loaded. Please refresh and try again.");
    const { ClientId, AuthorityUrl } = JSON.parse(stored);
    if (!ClientId || !AuthorityUrl) throw new Error("Tenant SSO configuration is incomplete (missing ClientId or AuthorityUrl).");
    if (_msalInstance && _activeClientId === ClientId) return _msalInstance;
    const msalConfig = {
        auth: {
            clientId:    ClientId,
            authority:   AuthorityUrl,
            redirectUri: window.location.origin + "/",
        },
        cache: { cacheLocation: "localStorage", storeAuthStateInCookie: false },
    };
    _msalInstance = new PublicClientApplication(msalConfig);
    await _msalInstance.initialize();
    _activeClientId = ClientId;
    return _msalInstance;
}

export async function startMicrosoftLogin() {
    const msalInstance = await getMsalInstance();
    if (window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname);
    }
    _redirectResultCache = undefined;
    // Clear any stale interaction lock before starting a new one.
    // Pass navigateToLoginRequestUrl: false so MSAL processes the stale result
    // in-place instead of navigating back to the originating page.
    try { await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false }); } catch (_) {}
    await msalInstance.loginRedirect({
        scopes: ["openid", "profile", "email"],
        prompt: "select_account",
    });
}

export async function processMicrosoftRedirectResult() {
    const msalInstance = await getMsalInstance();

    if (_redirectResultCache === undefined) {
        try {
            // CRITICAL: pass navigateToLoginRequestUrl: false explicitly.
            //
            // MSAL v5 RedirectClient reads this from options?.navigateToLoginRequestUrl,
            // NOT from config.auth.navigateToLoginRequestUrl (the config field is ignored).
            // Default is true, which makes MSAL compare the current URL ("/") against the
            // ORIGIN_URI stored when loginRedirect() was called (e.g. "/login/banker").
            // When they differ, MSAL caches the hash and navigates back to ORIGIN_URI,
            // returning null — causing the consistent auth failure on role-specific paths.
            _redirectResultCache = await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false }) ?? null;
        } catch (err) {
            _redirectResultCache = null;
            console.error("[MSAL] handleRedirectPromise threw:", err?.errorCode, err?.message, err);
            throw err;
        }
    }

    const redirectResult = _redirectResultCache;

    if (!redirectResult) {
        // Diagnostic: MSAL temp cache lives in sessionStorage, not localStorage.
        const sessionKeys = Object.keys(sessionStorage).filter(
            k => k.toLowerCase().includes("msal") || (_activeClientId && k.includes(_activeClientId))
        );
        console.error("[MSAL] handleRedirectPromise returned null.", {
            currentUrl:          window.location.href,
            hashPresent:         !!window.location.hash,
            hashPreview:         window.location.hash.substring(0, 120),
            sessionStorageKeys:  sessionKeys,
        });
        return null;
    }

    const backendResponse = await instance.post(SSO_VALIDATE_MICROSOFT_TOKEN, {
        IdToken:      redirectResult.idToken,
        AccessToken:  redirectResult.accessToken || "",
    });

    return {
        msalAccount: {
            username:       redirectResult.account?.username,
            name:           redirectResult.account?.name,
            tenantId:       redirectResult.account?.tenantId,
            localAccountId: redirectResult.account?.localAccountId,
            homeAccountId:  redirectResult.account?.homeAccountId,
            environment:    redirectResult.account?.environment,
        },
        idTokenClaims: redirectResult.idTokenClaims || {},
        idToken:       redirectResult.idToken,
        backendResult: backendResponse.data,
    };
}
